import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { DateTimePicker } from '@/components/DateTimePicker';
import { useNPSDSearch, NPSDSearchFilter } from '@/hooks/useNPSDSearch';

import moment from 'moment';

interface NPSDSearchPersonProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  initialValues?: Record<string, any>;
  showNewButton?: boolean;
  onView?: (item: any) => void;
  onAdd?: (item: any) => void;
  onLink?: (item: any) => void;
  onNew?: () => void;
}

export function NPSDSearchPerson({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  initialValues = {},
  showNewButton = false,
  onView,
  onAdd,
  onLink,
  onNew,
}: NPSDSearchPersonProps) {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [dob, setDob] = useState<Date | null>(null);
  const [driversLicenseNum, setDriversLicenseNum] = useState('');
  const [address, setAddress] = useState('');

  const {
    results, totalRecords, currentPage, pageSize, isLoading, hasSearched,
    showViewButton, showAddButton, showLinkButton,
    performSearch, clearResults, handlePageChange, handlePageSizeChange,
  } = useNPSDSearch({ entityApiName: 'Person', searchMode });

  const columns = useMemo(() => [
    { key: 'lastName', label: 'Last Name', field: 'lastName' },
    { key: 'firstName', label: 'First Name', field: 'firstName' },
    { key: 'middleName', label: 'Middle Name', field: 'middleName' },
    { key: 'dob', label: 'DOB', field: 'dob' },
    { key: 'driversLicenseNum', label: 'License #', field: 'driversLicenseNum' },
    { key: 'locFormattedAddress', label: 'Address', field: 'locFormattedAddress', fullWidth: true },
  ], []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (lastName) count++;
    if (firstName) count++;
    if (middleName) count++;
    if (dob) count++;
    if (driversLicenseNum) count++;
    if (address) count++;
    return count;
  }, [lastName, firstName, middleName, dob, driversLicenseNum, address]);

  // Apply initial values
  useEffect(() => {
    if (initialValues) {
      if (initialValues.lastName) setLastName(initialValues.lastName);
      if (initialValues.firstName) setFirstName(initialValues.firstName);
      if (initialValues.middleName) setMiddleName(initialValues.middleName);
      if (initialValues.dob) {
        const dobMoment = moment(initialValues.dob, ['MM/DD/YYYY', 'YYYY-MM-DD']);
        setDob(dobMoment.isValid() ? dobMoment.toDate() : null);
      }
      if (initialValues.driversLicenseNum) setDriversLicenseNum(initialValues.driversLicenseNum);
      if (initialValues.address) setAddress(initialValues.address);
    }
  }, [initialValues]);

  const buildFilters = useCallback((): NPSDSearchFilter[] => {
    const filters: NPSDSearchFilter[] = [];

    if (lastName) {
      filters.push({ field: 'lastName', operator: 'contains', value: lastName.trim() });
    }
    if (firstName) {
      filters.push({ field: 'firstName', operator: 'contains', value: firstName.trim() });
    }
    if (middleName) {
      filters.push({ field: 'middleName', operator: 'contains', value: middleName.trim() });
    }
    if (dob) {
      const dobMoment = moment.utc(dob);
      if (dobMoment.isValid()) {
        filters.push({ field: 'dob', operator: 'eq', value: dobMoment.format('DD MMM YYYY') });
      }
    }
    if (driversLicenseNum) {
      filters.push({ field: 'driversLicenseNum', operator: 'contains', value: driversLicenseNum.trim() });
    }
    if (address) {
      filters.push({ field: 'locFormattedAddress', operator: 'contains', value: address.trim() });
    }

    return filters;
  }, [lastName, firstName, middleName, dob, driversLicenseNum, address]);

  const doSearch = useCallback(() => {
    performSearch(buildFilters());
  }, [performSearch, buildFilters]);

  const clearSearch = useCallback(() => {
    setLastName('');
    setFirstName('');
    setMiddleName('');
    setDob(null);
    setDriversLicenseNum('');
    setAddress('');
    clearResults();
  }, [clearResults]);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleAdd = useCallback((item: any) => {
    onAdd?.(item);
  }, [onAdd]);

  const handleLink = useCallback((item: any) => {
    onLink?.(item);
  }, [onLink]);

  const handleNew = useCallback(() => {
    onNew?.();
  }, [onNew]);

  const handleRowClick = useCallback((item: any) => {
    if (searchMode === 'default') {
      handleView(item);
    } else if (showAddButton) {
      handleAdd(item);
    } else if (showLinkButton) {
      handleLink(item);
    }
  }, [searchMode, showAddButton, showLinkButton, handleView, handleAdd, handleLink]);

  const onPageChange = useCallback((page: number) => {
    handlePageChange(page, buildFilters());
  }, [handlePageChange, buildFilters]);

  const onPageSizeChange = useCallback((size: number) => {
    handlePageSizeChange(size, buildFilters());
  }, [handlePageSizeChange, buildFilters]);

  const formatDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '\u2014';
    const m = moment.utc(dateStr);
    if (!m.isValid()) return dateStr;
    return m.format('DD MMM YYYY');
  }, []);

  const getDisplayName = useCallback((item: any): string => {
    const parts: string[] = [];
    if (item.lastName) parts.push(item.lastName);
    if (item.firstName) parts.push(item.firstName);
    return parts.join(', ') || 'Unknown';
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      doSearch();
    }
  }, [doSearch]);

  const cellRenderers = useMemo(() => ({
    dob: (item: any) => formatDate(item.dob),
  }), [formatDate]);

  const renderCardTitle = useCallback((item: any) => getDisplayName(item), [getDisplayName]);

  return (
    <div className={`npsd-search-person ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={isLoading}
        onSearch={doSearch}
      >
        <div className="search-fields">
          <div className="field-row">
            <div className="field">
              <label>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Last Name"
              />
            </div>
            <div className="field">
              <label>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter First Name"
              />
            </div>
            <div className="field">
              <label>Middle Name</label>
              <input
                type="text"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Middle Name"
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>DOB</label>
              <DateTimePicker
                value={dob}
                onChange={setDob}
                type="date"
                defaultYear={2000}
                appendToBody={true}
                clearable={true}
              />
            </div>
            <div className="field">
              <label>License #</label>
              <input
                type="text"
                value={driversLicenseNum}
                onChange={(e) => setDriversLicenseNum(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter License #"
              />
            </div>
            <div className="field">
              <label>Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Address"
              />
            </div>
          </div>
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={doSearch} disabled={isLoading}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
          {showNewButton && (
            <button type="button" className="btn btn-success" onClick={handleNew}>
              <i className="fa fa-plus"></i> New
            </button>
          )}
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={results}
        loading={isLoading}
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        cellRenderers={cellRenderers}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

(window as any).NPSDSearchPersonReact = NPSDSearchPerson;
